/**
 * Error Utilities
 * Shared helpers for extracting error messages from various error types
 */

import { AxiosError } from 'axios';
import { serverErrorMessage } from './extractApiError';

interface ErrorOptions {
  /** Context string for logging (e.g., 'Error fetching user') */
  log?: string;
}

/**
 * True for any error axios raised: its own class, or the `isAxiosError` brand
 * axios stamps on every error it makes (a second axios copy or a mocked module
 * fails `instanceof` but keeps the brand).
 */
function isAxiosFailure(error: unknown): error is AxiosError {
  return (
    error instanceof AxiosError ||
    (!!error &&
      typeof error === 'object' &&
      (error as { isAxiosError?: unknown }).isAxiosError === true)
  );
}

/**
 * Extract a human-readable error message from an Axios error or generic error.
 *
 * The order is: the server's own words (`serverErrorMessage`: `errors`' first
 * field reason, then `message` unless it is a "Validation error" placeholder,
 * then `error`; the same order as `extractApiError`), else the caller's
 * fallback. An axios failure never
 * shows its own `message`: that is transport text ("Network Error", "timeout of
 * 15000ms exceeded", "canceled", "Request failed with status code 500") which
 * says nothing a person can act on, and the caller's fallback is the sentence
 * written for that screen. A plain `Error` our own code threw keeps its message.
 *
 * @param error - The error object (Axios error, Error, or unknown)
 * @param defaultMessage - Shown when the server sent no message: no response at
 *   all (network error, timeout, aborted), or a response without one
 * @param options - Optional settings (e.g., logging)
 * @returns A string error message
 *
 * @example
 * // Without logging
 * const message = getAxiosErrorMessage(error, 'Failed to fetch');
 *
 * @example
 * // With logging
 * const message = getAxiosErrorMessage(error, 'Failed to fetch', { log: 'UserService.getUser' });
 * // Logs: "[UserService.getUser]" + error object
 */
export function getAxiosErrorMessage(
  error: unknown,
  defaultMessage = 'An unknown error occurred',
  options?: ErrorOptions,
): string {
  // Log error if context provided
  if (options?.log) {
    console.error(`[${options.log}]`, error);
  }

  // Axios errors: the server's words when it sent some, else the caller's
  // fallback. Never axios's own message (see above).
  if (isAxiosFailure(error)) {
    return serverErrorMessage(error.response?.data) ?? defaultMessage;
  }

  // Handle standard Error objects our own code threw (an empty message says
  // nothing, so it gets the fallback)
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  // Handle error objects with response property (for type-narrowed cases)
  if (error && typeof error === 'object' && 'response' in error) {
    const message = serverErrorMessage((error as { response?: { data?: unknown } }).response?.data);
    if (message) return message;
  }

  return defaultMessage;
}
